import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { GetCustomerDocumentUrlsQuery } from "lib/generated/app-service-gql/graphql";
import { downloadFile } from "lib/utils";
import { useGraphQlClient } from "../useGraphQlClient";
import { CUSTOMER_DOCUMENTS_URLS } from "./useCustomerDocumentUrls";

export type DownloadDocParams = {
  id: string;
  fileName: string;
};

export function useDownloadCustomerDocument(
  options?: UseMutationOptions<void, unknown, DownloadDocParams, unknown>,
) {
  const { client } = useGraphQlClient();
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, DownloadDocParams>(async ({ id, fileName }) => {
    // Reuse cached URL if present
    let url = queryClient.getQueryData<string>(["customerDocUrl", id]);
    if (!url) {
      const result = await fetchCustomerDocumentUrls(client, [id]);
      url = result[0].documentUrl;
      queryClient.setQueryData(["customerDocUrl", id], url);
    }
    const blob = await fetchCustomerDocumentBlob(url);
    downloadFile(blob, fileName);
  }, options);
}

function toArrayBuffer(u: {
  buffer: ArrayBufferLike;
  byteOffset: number;
  byteLength: number;
}): ArrayBuffer {
  const { buffer, byteOffset, byteLength } = u;
  if (buffer instanceof ArrayBuffer) {
    return byteOffset === 0 && byteLength === buffer.byteLength
      ? buffer
      : buffer.slice(byteOffset, byteOffset + byteLength);
  }
  const view = new Uint8Array(buffer, byteOffset, byteLength);
  const copy = new Uint8Array(byteLength);
  copy.set(view);
  return copy.buffer;
}

async function downloadFilesAsZip(files: { fileName: string; blob: Blob }[], zipFileName: string) {
  const { zipSync } = await import("fflate");

  // Convert blobs to Uint8Array
  const fileMap: Record<string, Uint8Array> = {};
  for (const { fileName, blob } of files) {
    const arrayBuffer = await blob.arrayBuffer();
    fileMap[fileName] = new Uint8Array(arrayBuffer);
  }

  const zipData = zipSync(fileMap);

  const zipArrayBuffer = toArrayBuffer({
    buffer: zipData.buffer,
    byteOffset: zipData.byteOffset,
    byteLength: zipData.byteLength,
  });

  const zipBlob = new Blob([zipArrayBuffer], { type: "application/zip" });

  downloadFile(zipBlob, zipFileName);
}

type BulkDownloadParams = {
  documents: DownloadDocParams[];
  zipFileName: string;
};

export function useDownloadCustomerDocuments(
  options?: UseMutationOptions<void, unknown, BulkDownloadParams, unknown>,
) {
  const { client } = useGraphQlClient();
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, BulkDownloadParams>(async ({ documents, zipFileName }) => {
    const urlsToFetch: { fileName: string; id: string }[] = [];
    const fileUrls: Record<string, { fileName: string; url: string }> = {};
    for (const { id, fileName } of documents) {
      const url = queryClient.getQueryData<string>(["customerDocUrl", id]);
      if (url) {
        fileUrls[id] = { fileName, url };
      } else {
        urlsToFetch.push({ fileName, id });
      }
    }
    if (urlsToFetch.length) {
      const fetchedUrls = await fetchCustomerDocumentUrls(
        client,
        urlsToFetch.map((d) => d.id),
      );
      for (let i = 0; i < urlsToFetch.length; i++) {
        const { fileName, id } = urlsToFetch[i];
        const fetchedUrl = fetchedUrls.find((u) => u.id === id);
        if (!fetchedUrl) throw new Error(`Missing URL for document ${id}`);
        const url = fetchedUrl.documentUrl;
        queryClient.setQueryData(["customerDocUrl", id], url);
        fileUrls[id] = { fileName, url };
      }
    }

    const files = await Promise.all(
      Object.values(fileUrls).map(async ({ url, fileName }) => {
        const blob = await fetchCustomerDocumentBlob(url);
        return { fileName, blob };
      }),
    );
    await downloadFilesAsZip(files, zipFileName);
  }, options);
}

/**
 * Downloads the file as a Blob when given the S3 url
 * @param url S3 url
 * @returns document blob
 */
export async function fetchCustomerDocumentBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to download document");

  return await response.blob();
}

async function fetchCustomerDocumentUrls(
  client: ReturnType<typeof useGraphQlClient>["client"],
  ids: string[],
) {
  const resp = await client.request<GetCustomerDocumentUrlsQuery>(CUSTOMER_DOCUMENTS_URLS, {
    ids,
  });
  const urls = resp.getCustomerDocumentUrls.items;
  if (!urls.length) throw new Error(`Failed to retrieve document URLs by ids ${ids.join(", ")}`);
  return urls;
}
